import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  type Location,
} from "react-router-dom";
import { NavigationType, UNSAFE_LocationContext as LocationContext } from "react-router";
import IdleScreen from "../pages/idle";
import QRScanScreen from "../pages/qr-scan";
import MainScreen from "../pages/main";
import MagicScreen from "../pages/magic";
import CategoryScreen from "../pages/category";
import { SocketProvider } from "../context/SocketContext";
import KioskSessionManager from "../components/KioskSessionManager";
import TicketSessionManager from "../components/TicketSessionManager";
import GlobalPinHandler from "../compoment/GlobalPinHandler";
import PWAInstallPrompt from "../compoment/PWAInstallPrompt";
import RouteFrozenContext from "./routeTransition";

const SECRET_KEY =
  process.env.REACT_APP_TREE_OF_WISDOM_SECRET_KEY ||
  process.env.REACT_APP_RELEASE_ROOM_SECRET_KEY ||
  "";

const PAYMENT_SECRET_KEY = process.env.REACT_APP_KIOSK_SECRET_KEY || "";

/** Keep in sync with the stack animation duration in App.scss. */
const ROUTE_TRANSITION_MS = 462;

function routeDepth(pathname = "") {
  if (!pathname || pathname === "/") return 0;
  if (pathname === "/qr-scan") return 1;
  if (pathname === "/categories") return 2;
  if (pathname === "/main") return 3;
  if (pathname === "/magic") return 4;
  return 0;
}

function isBackNavigation(fromPath: string, toPath: string, navigationType: string) {
  if (navigationType === "POP") return true;
  return routeDepth(toPath) < routeDepth(fromPath);
}

function renderAppRoutes(location: Location) {
  return (
    <Routes location={location}>
      <Route path="/" element={<IdleScreen />} />
      <Route path="/qr-scan" element={<QRScanScreen />} />
      <Route path="/categories" element={<CategoryScreen />} />
      <Route path="/main" element={<MainScreen />} />
      <Route path="/magic" element={<MagicScreen />} />
    </Routes>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const displayedRef = useRef(location);
  const [displayed, setDisplayed] = useState(location);
  const [outgoing, setOutgoing] = useState<Location | null>(null);
  const [direction, setDirection] = useState<"forward" | "back">("forward");

  useLayoutEffect(() => {
    if (location.pathname === displayedRef.current.pathname) {
      displayedRef.current = location;
      setDisplayed(location);
      return;
    }

    const goingBack = isBackNavigation(
      displayedRef.current.pathname,
      location.pathname,
      navigationType
    );
    setDirection(goingBack ? "back" : "forward");
    setOutgoing(displayedRef.current);
    displayedRef.current = location;
    setDisplayed(location);
  }, [location, navigationType]);

  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = window.setTimeout(() => {
      setOutgoing(null);
    }, ROUTE_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing, displayed.pathname]);

  return (
    <div className="routeTransitionHost">
      {outgoing ? (
        <RouteFrozenContext.Provider value={true}>
          <LocationContext.Provider value={{ location: outgoing, navigationType: NavigationType.Pop }}>
            <div
              key={`out-${outgoing.pathname}`}
              className={`routeTransition routeTransition--exit-${direction}${outgoing.pathname === "/magic" ? " routeTransition--magic" : ""}`}
              aria-hidden="true"
            >
              {renderAppRoutes(outgoing)}
            </div>
          </LocationContext.Provider>
        </RouteFrozenContext.Provider>
      ) : null}
      <div
        key={`in-${displayed.pathname}`}
        className={["routeTransition", outgoing ? `routeTransition--enter-${direction}` : "", displayed.pathname === "/magic" ? "routeTransition--magic" : ""].filter(Boolean).join(" ")}
      >
        {renderAppRoutes(displayed)}
      </div>
    </div>
  );
}

const AppRoutes = () => {
  return (
    <SocketProvider
      secretKey={SECRET_KEY}
      paymentSecretKey={PAYMENT_SECRET_KEY}
    >
      <Router>
        <KioskSessionManager />
        <TicketSessionManager />
        <GlobalPinHandler />
        <PWAInstallPrompt />
        <AnimatedRoutes />
      </Router>
    </SocketProvider>
  );
};

export default AppRoutes;
